<script setup lang="ts">
import { onBeforeMount, ref, computed } from 'vue';
import { useForm } from 'vee-validate';

import api from '@/api';
import { defaultAdditionalFundRequired, defaultIncomingProperty, defaultLoanDetails } from '@/helpers/builder';
import {
  AdditionalFundsPurpose,
  depositTypeOptions,
  LoanType,
  LoanPurposeAdditionalFundsPurposeOptions,
  propertyPurposeOptions,
  stampDutyStatusOptions,
  CommercialAdditionalFundsPurposeOptions,
} from '@/helpers/const';
import { snakeCase } from '@/helpers/format';
import { useFeatureFlags } from '@/helpers/useFeatureFlags';
import { useUserQuoteProcess } from '@/helpers/useUserQuoteProcess';
import { buildAdditionalFundsRequiredProperties } from '@/helpers/additionalFundsRequiredProperties';
import { loanPurposeQuoteSchema } from '@/validation';

import type { AdditionalFundsRequiredDto, ListAdditionalFundsPurposes } from '@/api/backend/public';
import type { AdditionalFundsRequiredEntry, IncomingProperty, LoanDetails, Address } from '@/types';
import { UserQuoteProcessStep } from '@/types';

let listAdditionalFundsPurposes: ListAdditionalFundsPurposes = [];
const { state, loanApp, goBack, saveAndGoNext } = useUserQuoteProcess();
const { isCommercialQuoteFlowEnabled, isPurchaseOnlyLoanTypeEnabled } = useFeatureFlags();

const PURCHASE_ONLY_OPTION_TOOLTIP = 'Purchase only applications are for residential / commercial purposes only';
const {
  values,
  meta,
  submitCount,
  handleSubmit: validateSubmit,
} = useForm<AdditionalFundsRequiredEntry>({
  validationSchema: loanPurposeQuoteSchema,
  initialValues: {
    ...loanApp.value.additionalFundsRequiredProperties,
    ...loanApp.value.additionalFundsRequired,
    isAdditionalFundsRequired: undefined,
  },
  validateOnMount: false,
});

const isLoading = ref(false);

const fundsPurposeOptions = computed(() => {
  const { [AdditionalFundsPurpose.OTHER]: other, ...baseOptions } = LoanPurposeAdditionalFundsPurposeOptions;
  const merged = { ...baseOptions, ...CommercialAdditionalFundsPurposeOptions };
  if (isPurchaseOnlyLoanTypeEnabled()) {
    return {
      ...merged,
      [AdditionalFundsPurpose.PURCHASE_ONLY]: 'Purchase Only',
      [AdditionalFundsPurpose.OTHER]: other,
    };
  }
  return { ...merged, [AdditionalFundsPurpose.OTHER]: other };
});

const fundsPurposeOptionTooltips = computed<Record<string, string>>(() => {
  if (!isPurchaseOnlyLoanTypeEnabled()) return {} as Record<string, string>;
  return {
    [AdditionalFundsPurpose.PURCHASE_ONLY]: PURCHASE_ONLY_OPTION_TOOLTIP,
  };
});

const handleSubmit = validateSubmit(async (values) => {
  isLoading.value = true;
  const isPurchaseOnly = values.reasonForFundsOption === AdditionalFundsPurpose.PURCHASE_ONLY;
  const additionalFundsPurposeId = listAdditionalFundsPurposes?.find(
    (item) => snakeCase(item.title) === values.reasonForFundsOption,
  )?.id;
  const isVacantOrRetirement =
    values.reasonForFundsOption === AdditionalFundsPurpose.PURCHASE_VACANT_LAND ||
    values.reasonForFundsOption === AdditionalFundsPurpose.PURCHASE_RETIREMENT_LAND_LEASE_OR_COMMUNITY_HOME;

  // Only populate fundsRequiredAmount for non-vacant/retirement / non-purchase-only options
  const additionalFundsRequired: AdditionalFundsRequiredDto = Object.assign(defaultAdditionalFundRequired(), {
    fundsRequiredDate: isVacantOrRetirement ? values.settlementDate : isPurchaseOnly ? '' : values.fundsRequiredDate,
    fundsRequiredAmount: isVacantOrRetirement || isPurchaseOnly ? 0 : values.fundsRequiredAmount,
    additionalFundsPurposeId: additionalFundsPurposeId ?? '',
    description: values.description,
  });

  const isContributingFundsTowardsPurchase =
    ((values.reasonForFundsOption === AdditionalFundsPurpose.PURCHASE_RETIREMENT_LAND_LEASE_OR_COMMUNITY_HOME &&
      values.isThereContractForSale !== undefined) ||
      values.reasonForFundsOption === AdditionalFundsPurpose.OTHER ||
      values.reasonForFundsOption === AdditionalFundsPurpose.PURCHASE_VACANT_LAND) &&
    values.contributingFundsTowardsPurchase;
  const loanDetails: LoanDetails = Object.assign(defaultLoanDetails(), {
    savingsAmount: isContributingFundsTowardsPurchase ? values.fundsToContributeAmount : 0,
  });

  const emptyAddress = {
    addressLine: '',
    addressLine2: '',
    locality: '',
    state: '',
    postcode: '2000',
    countryCode: 'au',
  } as Address;

  const property: IncomingProperty = Object.assign(defaultIncomingProperty(), {
    settlementDate: additionalFundsRequired.fundsRequiredDate,
    address: values.propertyAddress ?? emptyAddress,
    contractForSale: values.isThereContractForSale ?? false,
    purchaseAmount: values.purchasePrice ?? 0,
    depositAmount: values.depositAmount ?? 0,
    depositType: values.depositType ?? 'cash_payment',
    stampDutyStatus: values.stampDutyStatus ?? 'already_paid',
    stampDuty: 0,
    transferFeeCovered: values.transferFeeCovered,
  });

  // Only a purchase purpose keeps the purchase property; hidden purchase answers are dropped (JS-10505).
  const additionalFundsRequiredProperties = buildAdditionalFundsRequiredProperties(values);

  const wasPurchaseOnly = loanApp.value.loanType === LoanType.PURCHASE_ONLY;
  const nextLoanType = isPurchaseOnly
    ? LoanType.PURCHASE_ONLY
    : wasPurchaseOnly
    ? LoanType.SINGLE_SECURITY
    : loanApp.value.loanType;
  const purchaseOnlyModeChanged = wasPurchaseOnly !== isPurchaseOnly;

  const incomingPropertiesForPurpose =
    isPurchaseOnly || nextLoanType === LoanType.SINGLE_SECURITY
      ? isPurchaseOnly && !purchaseOnlyModeChanged
        ? [...loanApp.value.incomingProperties]
        : []
      : [property];
  const loanTypeUpdate = nextLoanType !== loanApp.value.loanType ? { loanType: nextLoanType } : {};
  const propertyResetOnPurchaseOnlyToggle = purchaseOnlyModeChanged
    ? { outgoingProperties: [], supportingProperties: [], endDebt: 0 }
    : {};
  const exitStrategyResetLeavingPurchaseOnly =
    wasPurchaseOnly && !isPurchaseOnly ? { exitStrategy: undefined, exitStrategyDetails: undefined } : {};

  const updateData = {
    ...loanTypeUpdate,
    ...propertyResetOnPurchaseOnlyToggle,
    ...exitStrategyResetLeavingPurchaseOnly,
    loanDetails,
    additionalFundsRequired,
    additionalFundsRequiredProperties: additionalFundsRequiredProperties,
    incomingProperties: incomingPropertiesForPurpose,
  };

  try {
    if (isCommercialQuoteFlowEnabled()) {
      saveAndGoNext(updateData, { nextStep: UserQuoteProcessStep.IncomingProperties });
    } else {
      saveAndGoNext(updateData);
    }
  } finally {
    isLoading.value = false;
  }
});

const isReady = ref(false);
onBeforeMount(async () => {
  isReady.value = false;
  try {
    listAdditionalFundsPurposes = await api.backend.public.listAdditionalFundsPurposes();
  } finally {
    isReady.value = true;
  }
});
</script>

<template>
  <div>
    <div class="mb-8 pb-2">
      <h2 class="text-2xl font-medium">Tell us about the <BxHighlight>loan purpose</BxHighlight>?</h2>
    </div>
    <form style="width: 500px" @submit="handleSubmit">
      <div>
        <BxField v-slot="{ field }" name="reasonForFundsOption">
          <p v-if="!values.reasonForFundsOption" class="mb-3">Select all that apply.</p>
          <BxSelect
            v-bind="field"
            :label="values.reasonForFundsOption ? 'Loan purpose' : 'Purpose of the funds'"
            label-type="floating"
            :list-items="fundsPurposeOptions"
            :option-tooltips="fundsPurposeOptionTooltips"
            class="form-control"
            data-test-id="loan-purpose-reason-for-funds"
          />
        </BxField>
        <BxField
          v-if="
            values.reasonForFundsOption === AdditionalFundsPurpose.PURCHASE_RETIREMENT_LAND_LEASE_OR_COMMUNITY_HOME ||
            values.reasonForFundsOption === AdditionalFundsPurpose.PURCHASE_VACANT_LAND
          "
          v-slot="{ field }"
          name="propertyAddress"
        >
          <BxAddressInput
            v-bind="field"
            label="Property Address"
            label-type="floating"
            class="form-control"
            data-test-id="loan-purpose-property-address"
          />
        </BxField>
        <BxField
          v-if="values.reasonForFundsOption === AdditionalFundsPurpose.PURCHASE_VACANT_LAND"
          v-slot="{ field }"
          name="propertyPurpose"
        >
          <BxRadio
            v-bind="field"
            :options="propertyPurposeOptions"
            label="What is the purpose of this property?"
            class="form-control"
            block
            data-test-id="loan-purpose-property-purpose"
          />
        </BxField>
        <BxField
          v-if="
            values.reasonForFundsOption === AdditionalFundsPurpose.PURCHASE_RETIREMENT_LAND_LEASE_OR_COMMUNITY_HOME ||
            values.reasonForFundsOption === AdditionalFundsPurpose.PURCHASE_VACANT_LAND
          "
          v-slot="{ field }"
          name="isThereContractForSale"
        >
          <div class="mb-4 mt-4">
            <BxRadio
              v-bind="field"
              label="Is there a contract for sale?"
              label-type="default"
              class="form-control"
              boolean
              data-test-id="loan-purpose-contract-for-sale"
            />
          </div>
        </BxField>
        <template
          v-if="
            (values.reasonForFundsOption === AdditionalFundsPurpose.PURCHASE_VACANT_LAND ||
              values.reasonForFundsOption ===
                AdditionalFundsPurpose.PURCHASE_RETIREMENT_LAND_LEASE_OR_COMMUNITY_HOME) &&
            values.isThereContractForSale !== undefined
          "
        >
          <BxField v-slot="{ field }" name="settlementDate">
            <BxDatePicker
              v-bind="field"
              :label="values.isThereContractForSale ? 'Settlement date' : 'Estimated settlement date'"
              label-type="floating"
              placeholder="DD/MM/YYYY"
              class="form-control"
              :min-date="new Date()"
              data-test-id="loan-purpose-settlement-date"
            />
          </BxField>
          <BxField v-slot="{ field }" name="purchasePrice">
            <BxNumberInput
              v-bind="field"
              :label="values.isThereContractForSale ? 'Purchase price' : 'Estimated purchase price'"
              label-type="floating"
              leading-symbol="$"
              class="form-control"
              data-test-id="loan-purpose-purchase-price"
            />
          </BxField>
          <BxField v-slot="{ field }" name="depositAmount">
            <BxNumberInput
              v-bind="field"
              :label="values.isThereContractForSale ? 'Deposit amount' : 'Estimated deposit amount'"
              label-type="floating"
              leading-symbol="$"
              class="form-control"
              data-test-id="loan-purpose-deposit-amount"
            />
          </BxField>
          <BxField v-slot="{ field }" name="depositType">
            <BxRadio
              v-bind="field"
              :options="depositTypeOptions"
              :label="values.isThereContractForSale ? 'How was your deposit paid?' : 'How is the deposit being paid?'"
              label-type="default"
              class="form-control"
              data-test-id="loan-purpose-deposit-type"
            />
          </BxField>
          <BxField
            v-if="
              values.reasonForFundsOption !== AdditionalFundsPurpose.PURCHASE_RETIREMENT_LAND_LEASE_OR_COMMUNITY_HOME
            "
            v-slot="{ field }"
            name="stampDutyStatus"
          >
            <BxRadio
              v-bind="field"
              :options="stampDutyStatusOptions"
              label="How will stamp duty be paid?"
              label-type="default"
              class="form-control incoming-margin-top"
              block
              data-test-id="loan-purpose-stamp-duty-paid"
            />
          </BxField>
          <BxField v-slot="{ field }" name="transferFeeCovered">
            <BxRadio
              v-bind="field"
              :id="'transfer-fee-covered'"
              label="Would you like the transfer fee to be covered?"
              label-type="default"
              class="form-control incoming-margin-top"
              boolean
              data-test-id="loan-purpose-transfer-fee-covered"
            />
          </BxField>
        </template>
        <template
          v-if="
            values.reasonForFundsOption !== AdditionalFundsPurpose.PURCHASE_VACANT_LAND &&
            values.reasonForFundsOption !== AdditionalFundsPurpose.PURCHASE_RETIREMENT_LAND_LEASE_OR_COMMUNITY_HOME &&
            values.reasonForFundsOption !== AdditionalFundsPurpose.PURCHASE_ONLY
          "
        >
          <BxField v-if="values.reasonForFundsOption" v-slot="{ field }" name="fundsRequiredAmount">
            <BxNumberInput
              v-bind="field"
              label="Funds required"
              :max="999999999"
              label-type="floating"
              leading-symbol="$"
              class="form-control"
              data-test-id="loan-purpose-funds-required-amount"
            />
          </BxField>
          <BxField v-if="values.reasonForFundsOption" v-slot="{ field }" name="fundsRequiredDate">
            <BxDatePicker
              v-bind="field"
              label="Funds required by"
              label-type="floating"
              placeholder="DD/MM/YYYY"
              class="form-control"
              :min-date="new Date()"
              data-test-id="loan-purpose-funds-required-date"
            />
          </BxField>
        </template>
        <BxField
          v-if="
            values.reasonForFundsOption === AdditionalFundsPurpose.COSMETIC_IMPROVEMENTS_BEFORE_SELLING ||
            values.reasonForFundsOption === AdditionalFundsPurpose.OTHER
          "
          v-slot="{ field }"
          name="description"
        >
          <div class="mt-6">
            <p v-if="values.reasonForFundsOption === AdditionalFundsPurpose.COSMETIC_IMPROVEMENTS_BEFORE_SELLING">
              Explain the cosmetic improvement being done
            </p>
            <p v-if="values.reasonForFundsOption === AdditionalFundsPurpose.OTHER">
              Provide the details of the additional borrowing purposes
            </p>
            <BxTextarea class="mt-2" :rows="5" v-bind="field" data-test-id="loan-purpose-description" />
          </div>
        </BxField>
        <BxField
          v-if="
            values.reasonForFundsOption !== AdditionalFundsPurpose.PURCHASE_ONLY &&
            (((values.reasonForFundsOption ===
              AdditionalFundsPurpose.PURCHASE_RETIREMENT_LAND_LEASE_OR_COMMUNITY_HOME ||
              values.reasonForFundsOption === AdditionalFundsPurpose.PURCHASE_VACANT_LAND) &&
              values.isThereContractForSale !== undefined) ||
              values.reasonForFundsOption === AdditionalFundsPurpose.OTHER)
          "
          v-slot="{ field }"
          name="contributingFundsTowardsPurchase"
        >
          <div class="mt-4">
            <BxRadio
              v-bind="field"
              label="Is your client contributing funds towards the purchase?"
              label-type="default"
              class="form-control"
              boolean
              data-test-id="loan-purpose-contributing-funds"
            />
          </div>
        </BxField>
        <BxField v-if="values.contributingFundsTowardsPurchase" v-slot="{ field }" name="fundsToContributeAmount">
          <div class="mt-4">
            <BxNumberInput
              v-bind="field"
              label="Funds to contribute amount"
              label-type="floating"
              leading-symbol="$"
              class="form-control"
              data-test-id="loan-purpose-funds-to-contribute-amount"
            />
          </div>
        </BxField>
      </div>
      <BxErrorPanel v-show="!meta.valid && submitCount > 0" class="mt-6">
        Please review the error messages above.
      </BxErrorPanel>
      <div class="mt-6">
        <BxButton variant="primary" :loading="!isReady || isLoading" type="submit" data-test-id="loan-purpose-continue">
          Save and continue
        </BxButton>
        <BxButton
          v-if="state.nextEvents.includes('BACK')"
          variant="link"
          class="ml-4"
          data-test-id="loan-purpose-back"
          @click="goBack()"
        >
          Back
        </BxButton>
      </div>
    </form>
  </div>
</template>
